import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { initRunicWebGL } from "./runic-webgl.js";

gsap.registerPlugin(ScrollTrigger);

const doc = document;
const cleanups = [];
const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
const addCleanup = callback => { if (typeof callback === "function") cleanups.push(callback); };
const listen = (target, type, handler, options) => {
  target.addEventListener(type, handler, options);
  addCleanup(() => target.removeEventListener(type, handler, options));
};

const state = {
  initialized: false,
  reduced: reducedQuery.matches,
  menuOpen: false,
  lenis: null
};

function initSmoothScroll() {
  if (state.reduced) return;
  const lenis = new Lenis({ content: document.body });
  state.lenis = lenis;
  const tick = time => lenis.raf(time * 1000);
  gsap.ticker.add(tick);
  gsap.ticker.lagSmoothing(0);
  lenis.on("scroll", ScrollTrigger.update);
  addCleanup(() => { gsap.ticker.remove(tick); lenis.destroy(); state.lenis = null; });
}

function initHeader() {
  const header = doc.querySelector(".site-header");
  if (!header) return;
  const update = () => header.classList.toggle("wf-scrolled", window.scrollY > 24);
  update();
  listen(window, "scroll", update, { passive: true });
}

function initMenu() {
  const menu = doc.getElementById("site-menu");
  if (!menu) return;
  const triggers = [...doc.querySelectorAll('[aria-controls="site-menu"]')];
  const mobilePanel = menu.querySelector(".mobile-menu-inner");
  const desktopPanel = menu.querySelector(".menu-block")?.parentElement;
  const panels = [mobilePanel, desktopPanel].filter(Boolean);
  const desktopMedia = window.matchMedia("(min-width: 768px)");
  mobilePanel?.classList.add("wf-menu-mobile-panel");
  desktopPanel?.classList.add("wf-menu-desktop-panel");
  const closeButton = doc.createElement("button");
  closeButton.type = "button";
  closeButton.className = "wf-menu-close";
  closeButton.setAttribute("aria-label", "Menüyü kapat");
  closeButton.innerHTML = '<span aria-hidden="true">×</span>';
  menu.appendChild(closeButton);
  let lastTrigger = null;
  let timeline = null;

  const activePanel = () => desktopMedia.matches ? desktopPanel : mobilePanel;

  const activateCurrentPanel = () => {
    const active = activePanel();
    panels.forEach(panel => {
      const selected = panel === active;
      panel.toggleAttribute("inert", !selected);
      panel.style.visibility = selected ? "visible" : "hidden";
      panel.style.pointerEvents = selected && panel !== desktopPanel ? "auto" : "none";
      if (!selected) gsap.set(panel, { opacity: 0 });
    });
    desktopPanel?.querySelector(".menu-block")?.style.setProperty("pointer-events", "auto");
    return active;
  };

  const syncInteractionMode = () => {
    if (!state.menuOpen) return;
    activateCurrentPanel();
    if (desktopMedia.matches) {
      menu.removeAttribute("aria-modal");
      state.lenis?.start();
    } else {
      menu.setAttribute("aria-modal", "true");
      state.lenis?.stop();
    }
  };

  const setAria = open => {
    menu.setAttribute("aria-hidden", String(!open));
    triggers.forEach(trigger => {
      trigger.setAttribute("aria-expanded", String(open));
      trigger.setAttribute("aria-label", open ? "Menüyü kapat" : "Menüyü aç");
    });
  };
  const open = trigger => {
    if (state.menuOpen) return;
    lastTrigger = trigger;
    state.menuOpen = true;
    doc.body.classList.add("wf-menu-open");
    setAria(true);
    syncInteractionMode();
    const panel = activePanel();
    const block = panel?.querySelector(".menu-block");
    const links = panel?.querySelectorAll(".menu-route-link") || [];
    timeline?.kill();
    timeline = gsap.timeline({ defaults: { overwrite: true } })
      .to(panel, { opacity: 1, duration: state.reduced ? 0 : 0.55, ease: "power2.out" }, 0);
    if (block) {
      timeline.fromTo(block,
        { x: 30, scale: 0.97, rotate: 0.65, transformOrigin: "top right" },
        { x: 0, scale: 1, rotate: 0, duration: state.reduced ? 0 : 0.72, ease: "power3.out" }, 0);
    }
    timeline.fromTo(links, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: state.reduced ? 0 : 0.4, stagger: state.reduced ? 0 : 0.06, ease: "power1.out" }, 0.1);
    window.setTimeout(() => closeButton.focus(), state.reduced ? 0 : 120);
  };
  const close = () => {
    if (!state.menuOpen) return;
    state.menuOpen = false;
    setAria(false);
    menu.removeAttribute("aria-modal");
    const panel = activePanel();
    const block = panel?.querySelector(".menu-block");
    timeline?.kill();
    timeline = gsap.timeline({
      onComplete: () => panels.forEach(item => { item.setAttribute("inert", ""); item.style.visibility = "hidden"; item.style.pointerEvents = "none"; })
    }).to(panel, { opacity: 0, duration: state.reduced ? 0 : 0.38, ease: "power2.in" }, 0);
    if (block) timeline.to(block, { x: 24, scale: 0.98, rotate: 0.5, duration: state.reduced ? 0 : 0.42, ease: "power2.in" }, 0);
    doc.body.classList.remove("wf-menu-open");
    state.lenis?.start();
    lastTrigger?.focus();
  };
  triggers.forEach(trigger => listen(trigger, "click", () => state.menuOpen ? close() : open(trigger)));
  listen(closeButton, "click", close);
  menu.querySelectorAll("a").forEach(link => listen(link, "click", close));
  listen(doc, "keydown", event => {
    if (event.key === "Escape" && state.menuOpen) { event.preventDefault(); close(); return; }
    if (event.key !== "Tab" || !state.menuOpen || desktopMedia.matches) return;
    const focusable = [...activePanel().querySelectorAll('a[href],button:not([disabled])'), closeButton].filter(item => item.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && doc.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && doc.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  listen(desktopMedia, "change", syncInteractionMode);
  addCleanup(() => { timeline?.kill(); closeButton.remove(); });
}

function initHeadline() {
  const title = doc.querySelector("#hero .banner-title-block");
  const word = title?.querySelector("div:last-child span");
  if (!word) return;
  word.dataset.heroHeadlineWord = "true";
  const words = ["birleştirin", "hızlandırın", "sadeleştirin", "ölçekleyin", "yönetin"];
  let index = 0;
  let timer = null;
  let changeTimer = null;
  const cycle = () => {
    word.style.filter = "blur(8px)";
    word.style.opacity = "0";
    changeTimer = window.setTimeout(() => {
      index = (index + 1) % words.length;
      word.textContent = words[index];
      requestAnimationFrame(() => { word.style.filter = "blur(0px)"; word.style.opacity = "1"; });
    }, 500);
  };
  const start = () => { if (!state.reduced && timer === null) timer = window.setInterval(cycle, 3000); };
  start();
  addCleanup(() => { clearInterval(timer); clearTimeout(changeTimer); });
}

function initHero() {
  const hero = doc.getElementById("hero");
  const host = hero?.querySelector("[data-runic-webgl]");
  if (!hero || !host) return;
  const runic = initRunicWebGL(host, { reducedMotion: state.reduced });
  let holdTimer = null;
  let activePointerId = null;
  const insideLogo = (clientX, clientY) => {
    const rect = host.getBoundingClientRect();
    const size = Math.min(rect.height * 0.74, rect.width * 0.92);
    const centerX = rect.left + rect.width / 2 + (window.innerWidth >= 768 ? 180 : 0);
    const centerY = rect.top + rect.height / 2;
    return clientX >= centerX - size / 2 && clientX <= centerX + size / 2 && clientY >= centerY - size / 2 && clientY <= centerY + size / 2;
  };
  const cancel = () => {
    clearTimeout(holdTimer);
    holdTimer = null;
    const pointerId = activePointerId;
    activePointerId = null;
    hero.dataset.logoHoldActive = "false";
    runic.setHolding(false);
    if (pointerId !== null && hero.hasPointerCapture(pointerId)) hero.releasePointerCapture(pointerId);
  };
  listen(hero, "pointerdown", event => {
    if (state.reduced || !event.isPrimary || activePointerId !== null) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (event.target.closest("a,button") || !insideLogo(event.clientX, event.clientY)) return;
    activePointerId = event.pointerId;
    hero.dataset.logoHoldActive = "true";
    runic.setHolding(true);
    hero.setPointerCapture(event.pointerId);
    holdTimer = window.setTimeout(() => {
      const id = activePointerId;
      activePointerId = null;
      hero.dataset.logoHoldActive = "false";
      runic.setHolding(false);
      if (id !== null && hero.hasPointerCapture(id)) hero.releasePointerCapture(id);
      runic.advance();
    }, 2000);
  });
  listen(hero, "pointermove", event => { if (activePointerId === event.pointerId && !insideLogo(event.clientX, event.clientY)) cancel(); });
  ["pointerup", "pointerleave", "pointercancel", "lostpointercapture"].forEach(type => listen(hero, type, event => {
    if (activePointerId === event.pointerId) cancel();
  }));
  addCleanup(() => { cancel(); runic.destroy(); });
}

function initReveals() {
  const items = [...doc.querySelectorAll(".subpage-reveal,[data-service-card],[data-solution-detail],[data-process-step],.product-card")];
  if (state.reduced) { gsap.set(items, { autoAlpha: 1, y: 0 }); return; }
  items.forEach(item => {
    gsap.fromTo(item, { autoAlpha: 0, y: 28 }, {
      autoAlpha: 1, y: 0, duration: 0.85, ease: "power3.out",
      scrollTrigger: { trigger: item, start: "top 88%", once: true }
    });
  });
}

function initAccordions() {
  doc.querySelectorAll('button[aria-expanded][aria-controls]:not([aria-controls="site-menu"])').forEach(button => {
    if (button.closest("[data-closing-marquee]")) return;
    const panel = doc.getElementById(button.getAttribute("aria-controls"));
    if (!panel) return;
    const startsOpen = button.getAttribute("aria-expanded") === "true";
    panel.hidden = !startsOpen;
    panel.setAttribute("aria-hidden", String(!startsOpen));
    if (!startsOpen) panel.setAttribute("inert", "");
    listen(button, "click", () => {
      const open = button.getAttribute("aria-expanded") !== "true";
      button.setAttribute("aria-expanded", String(open));
      if (open) {
        panel.hidden = false;
        panel.removeAttribute("inert");
        panel.setAttribute("aria-hidden", "false");
        gsap.fromTo(panel, { height: 0, autoAlpha: 0 }, { height: "auto", autoAlpha: 1, duration: state.reduced ? 0 : 0.42, ease: "power2.out", onComplete: () => ScrollTrigger.refresh() });
      } else {
        gsap.to(panel, { height: 0, autoAlpha: 0, duration: state.reduced ? 0 : 0.32, ease: "power2.in", onComplete: () => {
          panel.hidden = true;
          panel.setAttribute("inert", "");
          panel.setAttribute("aria-hidden", "true");
          gsap.set(panel, { clearProps: "height,opacity,visibility" });
          ScrollTrigger.refresh();
        }});
      }
    });
  });
}

function initHomeScrollScenes() {
  if (state.reduced) return;
  const aboutHeading = doc.querySelector("#about h2");
  if (aboutHeading) gsap.fromTo(aboutHeading, { opacity: 0.55 }, { opacity: 1, ease: "none", scrollTrigger: { trigger: aboutHeading, start: "top 88%", end: "bottom 42%", scrub: true } });

  doc.querySelectorAll("[data-service-card]").forEach((card, index) => {
    gsap.fromTo(card, { y: 36, opacity: 0.12 }, { y: 0, opacity: 1, duration: 0.85, delay: (index % 3) * 0.09, ease: "power3.out", scrollTrigger: { trigger: card, start: "top 96%", once: true } });
    const art = card.querySelector("[data-art-parallax]");
    if (art && window.innerWidth >= 1024) gsap.fromTo(art, { y: 12 }, { y: -12, ease: "none", scrollTrigger: { trigger: card, start: "top bottom", end: "bottom top", scrub: 0.7 } });
  });

  const work = doc.getElementById("work");
  const track = work?.querySelector("[data-work-track]") || work?.firstElementChild;
  if (work && track) {
    const cards = [...track.querySelectorAll("[data-work-card]")];
    const media = gsap.matchMedia();
    let refreshFrame = null;

    const scheduleRefresh = () => {
      if (refreshFrame !== null) cancelAnimationFrame(refreshFrame);
      refreshFrame = requestAnimationFrame(() => {
        refreshFrame = null;
        state.lenis?.resize();
        ScrollTrigger.refresh();
      });
    };

    media.add("(min-width: 768px)", () => {
      const tween = gsap.to(track, {
        x: () => -Math.max(0, track.scrollWidth - window.innerWidth),
        ease: "none",
        scrollTrigger: {
          trigger: work,
          start: "top top",
          end: () => "+=" + Math.max(window.innerWidth, track.scrollWidth - window.innerWidth),
          pin: true,
          scrub: 0.65,
          invalidateOnRefresh: true,
          anticipatePin: 1
        }
      });
      return () => {
        tween.kill();
        gsap.set(track, { clearProps: "transform" });
      };
    });

    const resizeObserver = typeof ResizeObserver === "function" ? new ResizeObserver(scheduleRefresh) : null;
    cards.forEach(card => resizeObserver?.observe(card));
    track.querySelectorAll("img").forEach(image => {
      if (!image.complete) listen(image, "load", scheduleRefresh, { once: true });
    });
    listen(window, "resize", scheduleRefresh, { passive: true });
    listen(window, "orientationchange", scheduleRefresh, { passive: true });
    addCleanup(() => {
      if (refreshFrame !== null) cancelAnimationFrame(refreshFrame);
      resizeObserver?.disconnect();
      media.revert();
    });
  }
}

function initProcessSteps() {
  const list = doc.querySelector('[aria-label="Beş adımda çalışma sürecimiz"]');
  if (!list) return;
  const frame = list.parentElement;
  const items = [...list.querySelectorAll("[data-process-step]")];
  const markers = items.map(item => item.querySelector("[data-process-marker]"));
  const svg = frame?.querySelector("svg");
  const base = svg?.querySelector("[data-line-base]");
  const accents = svg ? [...svg.querySelectorAll("[data-line-accent]")] : [];
  const toggle = frame?.querySelector(".how-we-work_motionToggle__Rq6Fr");
  if (!frame || !svg || !base || markers.some(marker => !marker)) return;

  const STEP_DURATION = 6000;
  const GLIDE_DURATION = 1.5;
  let active = 0;
  let paused = state.reduced;
  let timer = null;
  let visible = false;
  let pointerHeld = false;
  let focusHeld = false;
  let desktop = window.matchMedia("(min-width: 1200px)").matches;
  let centers = [];
  let half = 0;
  let glide = null;
  let resizeFrame = 0;
  const cursor = { position: 0 };

  const clearTimer = () => {
    window.clearTimeout(timer);
    timer = null;
  };

  const canAutoplay = () => desktop && !state.reduced && visible && !doc.hidden && !pointerHeld && !focusHeld && !paused;

  const schedule = () => {
    clearTimer();
    if (!canAutoplay()) return;
    timer = window.setTimeout(() => {
      setActive(active + 1, true);
      schedule();
    }, STEP_DURATION);
  };

  const draw = () => {
    if (!centers.length) return;
    const position = cursor.position;
    const first = desktop ? centers[0].x : centers[0].y;
    const last = desktop ? centers[centers.length - 1].x : centers[centers.length - 1].y;
    const axis = desktop ? centers[0].y : centers[0].x;
    const shoulder = half + 17;
    const depth = half + 11;
    const start = Math.min(first, position - shoulder);
    const end = Math.max(last, position + shoulder);
    const point = (along, across) => desktop ? `${along},${across}` : `${across},${along}`;
    const dip = desktop ? axis + depth : axis - depth;
    const curve = `C ${point(position - half, axis)} ${point(position - half - 6, dip)} ${point(position, dip)} C ${point(position + half + 6, dip)} ${point(position + half, axis)} ${point(position + shoulder, axis)}`;
    base.setAttribute("d", `M ${point(start, axis)} L ${point(position - shoulder, axis)} ${curve} L ${point(end, axis)}`);
    accents.forEach(path => path.setAttribute("d", `M ${point(position - shoulder, axis)} ${curve}`));
  };

  const moveTo = (index, animate = true) => {
    if (!centers[index]) return;
    const destination = desktop ? centers[index].x : centers[index].y;
    glide?.kill();
    if (!animate || state.reduced) {
      cursor.position = destination;
      draw();
      return;
    }
    glide = gsap.to(cursor, {
      position: destination,
      duration: GLIDE_DURATION,
      ease: "power2.inOut",
      onUpdate: draw
    });
  };

  function setActive(index, animate = true) {
    active = (index + items.length) % items.length;
    items.forEach((item, itemIndex) => {
      const isActive = itemIndex === active;
      item.dataset.active = String(isActive);
      markers[itemIndex]?.setAttribute("aria-pressed", String(isActive));
      const fill = item.querySelector("[data-process-fill]");
      if (fill) fill.style.transform = `scaleY(${itemIndex < active ? 1 : 0})`;
    });
    moveTo(active, animate);
  }

  const measure = (animate = false) => {
    desktop = window.matchMedia("(min-width: 1200px)").matches;
    centers = markers.map((marker, index) => ({
      x: items[index].offsetLeft + marker.offsetLeft + marker.offsetWidth / 2,
      y: items[index].offsetTop + marker.offsetTop + marker.offsetHeight / 2
    }));
    half = markers[0].offsetWidth / 2;
    gsap.set(svg, {
      left: list.offsetLeft,
      top: list.offsetTop,
      width: list.offsetWidth,
      height: list.offsetHeight
    });
    svg.setAttribute("viewBox", `0 0 ${list.offsetWidth} ${list.offsetHeight}`);
    glide?.kill();
    cursor.position = desktop ? centers[active].x : centers[active].y;
    draw();
    frame.dataset.lineReady = "true";
    if (animate) moveTo(active, false);
    schedule();
  };

  const requestMeasure = () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => measure(false));
  };

  const setToggleState = () => {
    if (!toggle) return;
    toggle.setAttribute("aria-pressed", String(paused));
    toggle.setAttribute("aria-label", paused ? "Süreç animasyonunu başlat" : "Süreç animasyonunu durdur");
    toggle.innerHTML = paused
      ? '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="6 3 20 12 6 21 6 3"></polygon></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="14" y="3" width="5" height="18" rx="1"></rect><rect x="5" y="3" width="5" height="18" rx="1"></rect></svg>';
  };

  markers.forEach((marker, index) => {
    listen(marker, "click", () => { setActive(index, true); schedule(); });
    listen(marker, "pointerenter", event => {
      if (event.pointerType === "mouse") setActive(index, true);
    });
  });

  listen(frame, "pointerenter", event => {
    if (event.pointerType !== "mouse") return;
    pointerHeld = true;
    clearTimer();
  });
  listen(frame, "pointerleave", event => {
    if (event.pointerType !== "mouse") return;
    pointerHeld = false;
    schedule();
  });
  listen(frame, "focusin", () => {
    focusHeld = true;
    clearTimer();
  });
  listen(frame, "focusout", event => {
    if (frame.contains(event.relatedTarget)) return;
    focusHeld = false;
    schedule();
  });

  if (toggle) listen(toggle, "click", () => {
    paused = !paused;
    setToggleState();
    schedule();
  });

  listen(doc, "visibilitychange", schedule);

  const visibilityTrigger = ScrollTrigger.create({
    trigger: list,
    start: "top 80%",
    end: "bottom 20%",
    onToggle: self => {
      visible = self.isActive;
      schedule();
    }
  });

  const entranceTriggers = [];
  if (!state.reduced) {
    if (desktop) {
      const timeline = gsap.timeline({
        scrollTrigger: { trigger: list, start: "top 86%", once: true }
      });
      items.forEach((item, index) => {
        const targets = [markers[index], item.querySelector("[data-process-content]")].filter(Boolean);
        timeline.fromTo(targets, { y: 20, opacity: 0.3 }, {
          y: 0,
          opacity: 1,
          duration: 0.7,
          stagger: 0.05,
          ease: "power3.out"
        }, index * 0.12);
      });
      entranceTriggers.push(timeline);
    } else {
      items.forEach(item => {
        entranceTriggers.push(gsap.fromTo(item, { y: 16, opacity: 0.35 }, {
          y: 0,
          opacity: 1,
          duration: 0.65,
          ease: "power3.out",
          scrollTrigger: { trigger: item, start: "top 92%", once: true }
        }));
      });
    }
  }

  const resizeObserver = new ResizeObserver(requestMeasure);
  resizeObserver.observe(list);
  listen(window, "orientationchange", requestMeasure, { passive: true });

  accents.forEach(path => {
    path.style.strokeDasharray = "none";
    path.style.strokeDashoffset = "0";
  });
  setToggleState();
  setActive(0, false);
  requestAnimationFrame(() => measure(false));

  addCleanup(() => {
    clearTimer();
    cancelAnimationFrame(resizeFrame);
    glide?.kill();
    resizeObserver.disconnect();
    visibilityTrigger.kill();
    entranceTriggers.forEach(animation => animation.kill());
    delete frame.dataset.lineReady;
  });
}

function initSolutionScenes() {
  doc.querySelectorAll("[data-solution-scene]").forEach(scene => {
    if (state.reduced) return;
    gsap.fromTo(scene, { opacity: 0.25 }, { opacity: 1, duration: 1.2, ease: "power2.out", scrollTrigger: { trigger: scene, start: "top 96%", once: true } });
    const idle = gsap.timeline({ paused: true, repeat: -1 })
      .fromTo(scene.querySelectorAll("[data-scene-orbit]"), { strokeDashoffset: 0 }, { strokeDashoffset: -870, duration: 11, ease: "none" })
      .fromTo(scene.querySelectorAll('path[stroke-dasharray="30 850"]'), { strokeDashoffset: 0 }, { strokeDashoffset: -880, duration: 10, ease: "none" }, 0)
      .to(scene.querySelectorAll("[data-scene-node]"), { opacity: 0.9, duration: 1.6, stagger: 0.65, yoyo: true, repeat: 1, ease: "sine.inOut" }, 0);
    ScrollTrigger.create({ trigger: scene, start: "top bottom", end: "bottom top", onEnter: () => idle.play(), onEnterBack: () => idle.play(), onLeave: () => idle.pause(), onLeaveBack: () => idle.pause() });
    const art = scene.querySelector("[data-scene-art]");
    if (art && window.innerWidth >= 1024) gsap.fromTo(art, { y: 6 }, { y: -18, ease: "none", scrollTrigger: { trigger: scene.closest("section") || scene, start: "top 78%", end: "bottom 24%", scrub: 2.8 } });
  });
}

const CODE_STREAM_CHARACTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
const CODE_STREAM_LENGTH = 2000;
const CODE_POINTER_LERP = 0.1;

function createCodeStream(length = CODE_STREAM_LENGTH) {
  let result = "";
  for (let index = 0; index < length; index += 1) {
    result += CODE_STREAM_CHARACTERS[Math.floor(Math.random() * CODE_STREAM_CHARACTERS.length)];
  }
  return result;
}

function initCodeCards() {
  doc.querySelectorAll(".animated-code-card").forEach(card => {
    const effect = card.querySelector(".animated-code-card__effect");
    if (!effect) return;

    // Upgrade the static ASPX markup to stable motion hooks without relying on
    // generated CSS-module class names.
    card.dataset.codeCard = "true";
    effect.dataset.codeEffect = "true";
    effect.textContent = createCodeStream();

    let frame = null;
    let visible = true;
    let pointerInside = false;
    const target = { x: 0, y: 0 };
    const rendered = { x: 0, y: 0 };

    const setPosition = (x, y) => {
      card.style.setProperty("--code-x", `${x}px`);
      card.style.setProperty("--code-y", `${y}px`);
    };
    const cancelFrame = () => {
      if (frame === null) return;
      cancelAnimationFrame(frame);
      frame = null;
    };
    const renderPointer = () => {
      frame = null;
      if (!visible || document.hidden || !pointerInside) return;
      rendered.x += (target.x - rendered.x) * CODE_POINTER_LERP;
      rendered.y += (target.y - rendered.y) * CODE_POINTER_LERP;
      setPosition(rendered.x, rendered.y);
      if (Math.hypot(target.x - rendered.x, target.y - rendered.y) > 0.35) {
        frame = requestAnimationFrame(renderPointer);
      }
    };
    const showEffect = () => gsap.to(card, { "--code-effect-opacity": 1, duration: 0.5, ease: "power3.out", overwrite: true });
    const hideEffect = () => {
      cancelFrame();
      gsap.to(card, { "--code-effect-opacity": 0, duration: 0.5, ease: "power3.out", overwrite: true });
    };

    listen(card, "pointerenter", event => {
      if (event.pointerType === "touch") return;
      const rect = card.getBoundingClientRect();
      target.x = event.clientX - rect.left;
      target.y = event.clientY - rect.top;
      rendered.x = target.x;
      rendered.y = target.y;
      pointerInside = true;
      setPosition(target.x, target.y);
      showEffect();
    });
    listen(card, "pointermove", event => {
      if (event.pointerType === "touch") return;
      const rect = card.getBoundingClientRect();
      target.x = event.clientX - rect.left;
      target.y = event.clientY - rect.top;
      effect.textContent = createCodeStream();
      if (state.reduced) {
        rendered.x = target.x;
        rendered.y = target.y;
        setPosition(target.x, target.y);
      } else if (frame === null && visible && !document.hidden) {
        frame = requestAnimationFrame(renderPointer);
      }
    });
    listen(card, "pointerleave", () => {
      pointerInside = false;
      hideEffect();
    });
    listen(card, "focusin", () => {
      const x = card.clientWidth / 2;
      const y = card.clientHeight / 2;
      target.x = rendered.x = x;
      target.y = rendered.y = y;
      setPosition(x, y);
      showEffect();
    });
    listen(card, "focusout", event => {
      if (!card.contains(event.relatedTarget)) hideEffect();
    });

    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      if (!visible) cancelFrame();
      else if (pointerInside && !state.reduced && frame === null && !document.hidden) frame = requestAnimationFrame(renderPointer);
    });
    observer.observe(card);
    listen(doc, "visibilitychange", () => {
      if (document.hidden) cancelFrame();
      else if (visible && pointerInside && !state.reduced && frame === null) frame = requestAnimationFrame(renderPointer);
    });
    addCleanup(() => {
      observer.disconnect();
      cancelFrame();
      gsap.killTweensOf(card);
    });
  });
}

function initMarquees() {
  doc.querySelectorAll("[data-closing-marquee]").forEach(section => {
    const button = section.querySelector("button");
    const tracks = [...section.querySelectorAll("[data-marquee-track]")];
    const narrow = window.matchMedia("(max-width: 767px)");
    let paused = state.reduced;
    let inView = false;
    let disposed = false;

    const updateControl = () => {
      section.dataset.paused = String(paused);
      button?.setAttribute("aria-label", paused ? "Yazı hareketini devam ettir" : "Yazı hareketini duraklat");
      const label = button?.querySelector("span");
      if (label) label.textContent = paused ? "Devam ettir" : "Hareketi durdur";
      const icon = button?.querySelector("svg");
      if (icon) {
        icon.setAttribute("class", paused ? "lucide lucide-play" : "lucide lucide-pause");
        icon.innerHTML = paused
          ? '<polygon points="6 3 20 12 6 21 6 3"></polygon>'
          : '<rect x="14" y="3" width="5" height="18" rx="1"></rect><rect x="5" y="3" width="5" height="18" rx="1"></rect>';
      }
    };
    const measure = () => {
      if (disposed) return;
      const speed = narrow.matches ? 25 : 38;
      tracks.forEach(track => {
        const group = track.firstElementChild;
        if (!group) return;
        const width = group.getBoundingClientRect().width;
        if (width > 0) track.style.setProperty("--marquee-duration", `${width / speed}s`);
      });
    };
    const updatePlayback = () => {
      section.dataset.active = String(inView && !document.hidden);
    };

    updateControl();
    measure();
    if (button) listen(button, "click", () => {
      paused = !paused;
      updateControl();
    });

    const visibilityObserver = new IntersectionObserver(([entry]) => {
      inView = Boolean(entry?.isIntersecting);
      updatePlayback();
    });
    visibilityObserver.observe(section);

    const resizeObserver = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    if (resizeObserver) {
      resizeObserver.observe(section);
      tracks.forEach(track => {
        if (track.firstElementChild) resizeObserver.observe(track.firstElementChild);
      });
    } else {
      listen(window, "resize", measure, { passive: true });
    }
    listen(doc, "visibilitychange", updatePlayback);
    listen(narrow, "change", measure);
    if (doc.fonts?.ready) void doc.fonts.ready.then(measure);

    addCleanup(() => {
      disposed = true;
      visibilityObserver.disconnect();
      resizeObserver?.disconnect();
    });
  });
}

function initFooterCanvas() {
  const canvas = doc.querySelector("footer canvas");
  if (!canvas?.getContext) return;
  const context = canvas.getContext("2d");
  if (!context) return;
  let frame = null;
  let visible = false;
  let start = performance.now();
  const draw = now => {
    frame = null;
    if (!visible || document.hidden || state.reduced) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
    const width = Math.max(1, Math.floor(rect.width * ratio));
    const height = Math.max(1, Math.floor(rect.height * ratio));
    if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, rect.width, rect.height);
    const t = (now - start) / 1000;
    const step = Math.max(42, rect.width / 24);
    context.lineWidth = 1;
    for (let row = -2; row < rect.height / step + 3; row++) {
      context.beginPath();
      for (let x = -step; x <= rect.width + step; x += 12) {
        const y = row * step + Math.sin(x * 0.012 + t * 0.55 + row * 0.7) * 9;
        if (x === -step) context.moveTo(x, y); else context.lineTo(x, y);
      }
      context.strokeStyle = "rgba(190,151,80,.13)";
      context.stroke();
    }
    for (let column = -4; column < rect.width / step + 4; column++) {
      context.beginPath();
      for (let y = 0; y <= rect.height; y += 12) {
        const x = column * step + y * 0.55 + Math.sin(y * 0.014 + t * 0.45 + column) * 8;
        if (y === 0) context.moveTo(x, y); else context.lineTo(x, y);
      }
      context.strokeStyle = "rgba(190,151,80,.10)";
      context.stroke();
    }
    frame = requestAnimationFrame(draw);
  };
  const play = () => { if (frame === null && visible && !document.hidden && !state.reduced) frame = requestAnimationFrame(draw); };
  const stop = () => { if (frame !== null) cancelAnimationFrame(frame); frame = null; };
  const observer = new IntersectionObserver(([entry]) => { visible = Boolean(entry?.isIntersecting); visible ? play() : stop(); });
  observer.observe(canvas);
  listen(doc, "visibilitychange", () => document.hidden ? stop() : play());
  addCleanup(() => { observer.disconnect(); stop(); });
}

function initContactMapModal() {
  const trigger = doc.querySelector("[data-map-modal-open]");
  if (!trigger) return;

  const mapUrl = "https://maps.google.com/maps?hl=tr&q=Kapani%20%C4%B0%C5%9Fhan%C4%B1%2C%20Konak%2C%20%C4%B0zmir&z=18&ie=UTF8&iwloc=B&output=embed";
  const modal = doc.createElement("div");
  modal.id = "contact-map-modal";
  modal.className = "privacy-modal contact-map-modal";
  modal.hidden = true;
  modal.innerHTML = `
    <button type="button" class="privacy-modal__backdrop" data-map-close aria-label="Haritayı kapat"></button>
    <section class="privacy-modal__panel" role="dialog" aria-modal="true" aria-labelledby="contact-map-title" aria-describedby="contact-map-description">
      <header class="privacy-modal__header">
        <div>
          <p class="route-eyebrow">Adres · Google Maps</p>
          <h2 id="contact-map-title">Korel Yazılım</h2>
          <p id="contact-map-description" class="contact-map-modal__description">Kapani İş Merkezi No: 36 / 711, Konak / İZMİR</p>
        </div>
        <button type="button" class="privacy-modal__close" data-map-close aria-label="Haritayı kapat">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 5l14 14M19 5 5 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
        </button>
      </header>
      <div class="contact-map-modal__frame">
        <iframe title="Korel Yazılım Google Maps haritası" data-map-frame data-src="${mapUrl}" loading="lazy" referrerpolicy="no-referrer-when-downgrade" allowfullscreen></iframe>
      </div>
      <footer class="privacy-modal__footer"><button type="button" class="route-button route-button--primary" data-map-close>Kapat</button></footer>
    </section>`;
  doc.body.append(modal);

  const frame = modal.querySelector("[data-map-frame]");
  const closeButtons = [...modal.querySelectorAll("[data-map-close]")];
  let lastFocused = null;
  let closeTimer = null;

  const focusable = () => [...modal.querySelectorAll('button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])')]
    .filter(element => !element.hidden && element.getClientRects().length);
  const open = event => {
    event?.preventDefault();
    window.clearTimeout(closeTimer);
    lastFocused = doc.activeElement;
    modal.hidden = false;
    doc.body.classList.add("wf-map-open");
    state.lenis?.stop();
    requestAnimationFrame(() => {
      if (!frame.hasAttribute("src")) frame.setAttribute("src", frame.dataset.src);
      modal.classList.add("is-open");
      modal.querySelector(".privacy-modal__close")?.focus();
    });
  };
  const close = () => {
    if (modal.hidden) return;
    modal.classList.remove("is-open");
    doc.body.classList.remove("wf-map-open");
    state.lenis?.start();
    closeTimer = window.setTimeout(() => {
      modal.hidden = true;
      frame.removeAttribute("src");
      if (lastFocused instanceof HTMLElement) lastFocused.focus();
    }, 260);
  };
  const handleKeydown = event => {
    if (modal.hidden) return;
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== "Tab") return;
    const elements = focusable();
    if (!elements.length) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && doc.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && doc.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  listen(trigger, "click", open);
  closeButtons.forEach(button => listen(button, "click", close));
  listen(doc, "keydown", handleKeydown);
  addCleanup(() => {
    window.clearTimeout(closeTimer);
    doc.body.classList.remove("wf-map-open");
    modal.remove();
  });
}

function initContactPage() {
  const form = doc.querySelector("#contact-form, #meeting-request-form");
  if (!form) return;
  const isMeetingRequest = form.dataset.formKind === "meeting";

  doc.body.classList.add("wf-contact-page");
  doc.querySelector("#main-content > .subpage-hero")?.remove();

  const section = form.closest("section");
  const layout = form.closest(".grid");
  const introduction = layout?.firstElementChild;
  if (section) section.classList.add("contact-form-section");
  if (introduction) {
    const eyebrow = introduction.querySelector(".route-eyebrow");
    const heading = introduction.querySelector(".route-section-title");
    const copy = introduction.querySelector(".route-copy");
    const details = introduction.querySelector(".border-t");

    if (eyebrow) eyebrow.textContent = isMeetingRequest ? "Online Görüşme" : "İletişim";
    if (heading) {
      heading.id = "contact-page-title";
      heading.textContent = isMeetingRequest ? "Görüşme talebinizi planlayalım." : "İhtiyaçlarınızı birlikte netleştirelim.";
      section?.setAttribute("aria-labelledby", heading.id);
    }
    if (copy) copy.textContent = isMeetingRequest
      ? "Görüşmek istediğiniz konuyu, tercih ettiğiniz tarih ve saati bize iletin. Ekibimiz uygunluğu doğrulamak için sizinle iletişime geçsin."
      : "Yeni bir sistem, mevcut yazılımın yenilenmesi, entegrasyon veya paket ürün ihtiyacınız için bize ulaşın.";
    if (details && !details.querySelector("[data-contact-address]")) {
      const address = doc.createElement("p");
      address.className = "mt-6";
      address.dataset.contactAddress = "true";
      address.innerHTML = '<span class="route-mini-label block">Adres</span><address class="contact-address"><button type="button" class="contact-address-link" data-map-modal-open aria-haspopup="dialog" aria-controls="contact-map-modal" aria-label="Kapani İş Merkezi No: 36 / 711, Konak / İzmir konumunu haritada göster"><span class="contact-address-line">Kapani İş Merkezi No: 36 / 711</span><span class="contact-address-line">Konak / İZMİR</span></button></address>';
      details.append(address);
    }
  }

  initContactMapModal();

  const trigger = form.querySelector("[data-privacy-fallback]");
  if (!trigger) return;

  trigger.type = "button";
  trigger.classList.add("contact-privacy-trigger");
  trigger.dataset.privacyOpen = "true";
  trigger.setAttribute("aria-haspopup", "dialog");
  trigger.setAttribute("aria-controls", "privacy-modal");
  trigger.textContent = "Aydınlatma Metni";

  const modal = doc.createElement("div");
  modal.id = "privacy-modal";
  modal.className = "privacy-modal";
  modal.dataset.privacyModal = "true";
  modal.hidden = true;
  modal.innerHTML = `
    <button type="button" class="privacy-modal__backdrop" data-privacy-close aria-label="Aydınlatma metnini kapat"></button>
    <section class="privacy-modal__panel" role="dialog" aria-modal="true" aria-labelledby="privacy-modal-title" aria-describedby="privacy-modal-intro">
      <header class="privacy-modal__header">
        <div>
          <p class="route-eyebrow">Gizlilik · KVKK</p>
          <h2 id="privacy-modal-title">Aydınlatma Metni</h2>
        </div>
        <button type="button" class="privacy-modal__close" data-privacy-close aria-label="Aydınlatma metnini kapat">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 5l14 14M19 5 5 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
        </button>
      </header>
      <div class="privacy-modal__content" data-lenis-prevent data-lenis-prevent-wheel tabindex="0" aria-label="Aydınlatma metni içeriği">
        <p id="privacy-modal-intro" class="privacy-modal__lead">Bu metin, Korel Yazılım ile iletişime geçtiğinizde paylaştığınız kişisel verilerin nasıl ele alındığını açıklar.</p>
        <section><span>01</span><div><h3>Veri sorumlusu</h3><p>6698 sayılı Kişisel Verilerin Korunması Kanunu kapsamında veri sorumlusu Korel Yazılım’dır. Sorularınız ve başvurularınız için <a href="mailto:info@korelyazilim.net">info@korelyazilim.net</a> adresini kullanabilirsiniz.</p></div></section>
<section><span>02</span><div><h3>İşlenen kişisel veriler</h3><p>İletişim veya görüşme talep formunu kullandığınızda ad soyad, şirket, kurumsal e-posta, isteğe bağlı telefon, ilgilendiğiniz konu, mesaj alanında paylaştığınız bilgiler ve görüşme talebinde seçtiğiniz tarih ile saat işlenir. Formun güvenliği için gerekli sınırlı teknik kayıtlar da barındırma altyapısı tarafından tutulabilir.</p></div></section>
        <section><span>03</span><div><h3>İşleme amaçları</h3><p>Verileriniz talebinizi değerlendirmek, size geri dönüş yapmak, olası proje veya hizmet kapsamını belirlemek, iletişim güvenliğini sağlamak ve talebinizle bağlantılı kayıtları yönetmek amacıyla kullanılır. Bu form üzerinden reklam veya toplu pazarlama izni alınmaz.</p></div></section>
        <section><span>04</span><div><h3>Toplama yöntemi ve hukuki sebep</h3><p>Veriler, iletişim formunu doldurmanız veya doğrudan e-posta ya da telefon yoluyla bize ulaşmanız üzerine elektronik ortamda elde edilir. İşleme; talebinize göre bir sözleşmenin kurulması veya ifasıyla doğrudan ilgili olma ve temel haklarınıza zarar vermemek kaydıyla iletişim talebini yanıtlamaya yönelik meşru menfaat hukuki sebeplerine dayanır.</p></div></section>
        <section><span>05</span><div><h3>Aktarım ve saklama</h3><p>Verileriniz yalnızca iletişim ve teknik işletim için gerekli olduğu ölçüde yetkili Korel ekipleri ile barındırma, e-posta veya form altyapısı hizmeti sağlayan tedarikçiler tarafından işlenebilir; mevzuat gerektirirse yetkili kamu kurumlarına aktarılabilir. Kayıtlar gerekli süreyle sınırlı tutulur ve süre sonunda güvenli biçimde silinir, yok edilir veya anonim hale getirilir.</p></div></section>
        <section><span>06</span><div><h3>Haklarınız ve başvuru</h3><p>KVKK’nın 11. maddesi kapsamında kişisel verilerinizin işlenip işlenmediğini öğrenme, bilgi talep etme, işleme amacını ve aktarılan kişileri öğrenme, eksik veya yanlış verilerin düzeltilmesini isteme, yasal şartları oluştuğunda silme veya yok etme talebinde bulunma ve kanuna aykırı işleme nedeniyle zararın giderilmesini isteme haklarına sahipsiniz.</p><p>Talebinizi açıkça belirterek <a href="mailto:info@korelyazilim.net">info@korelyazilim.net</a> adresinden bize ulaşabilirsiniz.</p></div></section>
        <p class="privacy-modal__updated">Son güncelleme: 23 Ağustos 2026</p>
      </div>
      <footer class="privacy-modal__footer"><button type="button" class="route-button route-button--primary" data-privacy-close>Kapat</button></footer>
    </section>`;
  doc.body.append(modal);

  const panel = modal.querySelector(".privacy-modal__panel");
  const content = modal.querySelector(".privacy-modal__content");
  const closeButtons = [...modal.querySelectorAll("[data-privacy-close]")];
  let lastFocused = null;
  let closeTimer = null;

  const focusable = () => [...modal.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
    .filter(element => !element.hidden && element.getClientRects().length);
  const open = event => {
    event?.preventDefault();
    event?.stopPropagation();
    window.clearTimeout(closeTimer);
    lastFocused = doc.activeElement;
    modal.hidden = false;
    doc.body.classList.add("wf-privacy-open");
    state.lenis?.stop();
    content.scrollTop = 0;
    requestAnimationFrame(() => {
      modal.classList.add("is-open");
      modal.querySelector(".privacy-modal__close")?.focus();
    });
  };
  const close = () => {
    if (modal.hidden) return;
    modal.classList.remove("is-open");
    doc.body.classList.remove("wf-privacy-open");
    state.lenis?.start();
    closeTimer = window.setTimeout(() => {
      modal.hidden = true;
      if (lastFocused instanceof HTMLElement) lastFocused.focus();
    }, 260);
  };
  const handleKeydown = event => {
    if (modal.hidden) return;
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== "Tab") return;
    const elements = focusable();
    if (!elements.length) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && doc.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && doc.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  listen(trigger, "click", open);
  closeButtons.forEach(button => listen(button, "click", close));
  listen(panel, "wheel", event => {
    event.stopPropagation();
    if (content.contains(event.target)) return;
    event.preventDefault();
    content.scrollTop += event.deltaY;
  }, { passive: false });
  listen(panel, "touchmove", event => event.stopPropagation(), { passive: true });
  listen(panel, "click", event => event.stopPropagation());
  listen(doc, "keydown", handleKeydown);
  addCleanup(() => {
    window.clearTimeout(closeTimer);
    doc.body.classList.remove("wf-contact-page", "wf-privacy-open");
    modal.remove();
  });
}

function initHomeProductLinks() {
  const products = {
    "korel-depo": {
      name: "Korel Depo",
      website: "https://koreldepo.com",
      instagram: "https://www.instagram.com/koreldepo"
    },
    "korel-dental": {
      name: "Korel Dental",
      website: "https://koreldental.com",
      instagram: "https://www.instagram.com/koreldental"
    },
    dtech: {
      name: "DTech Service Management",
      website: "https://dtechsm.com",
      instagram: "https://www.instagram.com/dtechsm"
    }
  };

  doc.querySelectorAll("#work .work-product-card").forEach(card => {
    let showcase = card.querySelector(".product-showcase[data-product]");
    if (!showcase) return;

    const product = products[showcase.dataset.product];
    if (!product) return;

    if (showcase.matches("a")) {
      const staticShowcase = doc.createElement("div");
      [...showcase.attributes].forEach(attribute => {
        if (attribute.name !== "href" && attribute.name !== "aria-label") {
          staticShowcase.setAttribute(attribute.name, attribute.value);
        }
      });
      while (showcase.firstChild) staticShowcase.appendChild(showcase.firstChild);
      showcase.replaceWith(staticShowcase);
      showcase = staticShowcase;
    }

    if (card.querySelector(".work-product-links")) return;
    const detailLink = card.querySelector(":scope > .route-text-link");
    if (!detailLink) return;

    const linkRow = doc.createElement("div");
    linkRow.className = "work-product-links";

    const externalLinks = doc.createElement("nav");
    externalLinks.className = "work-product-external-links";
    externalLinks.setAttribute("aria-label", `${product.name} dış bağlantıları`);

    [
      ["Web sitesi", product.website],
      ["Instagram", product.instagram]
    ].forEach(([label, href]) => {
      const link = doc.createElement("a");
      link.href = href;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.className = "work-product-external-link";
      link.setAttribute("aria-label", `${product.name} ${label} bağlantısını yeni sekmede aç`);
      link.innerHTML = `<span>${label}</span><span aria-hidden="true">↗</span>`;
      externalLinks.appendChild(link);
    });

    detailLink.replaceWith(linkRow);
    linkRow.append(detailLink, externalLinks);
  });
}

function initContactForm() {
  const form = doc.querySelector("form[data-form-kind]");
  if (!form) return;
  const isMeetingRequest = form.dataset.formKind === "meeting";
  const endpoint = form.dataset.endpoint || "/api/contact";
  const submitLabel = form.dataset.submitLabel || "Talebi gönder";
  const status = form.querySelector(".form-status");
  const submitButton = form.querySelector('button[type="submit"]');
  const topicSelect = form.elements.topic;
  const topics = topicSelect ? [...topicSelect.options].map(item => item.value) : [];
  const requestedTopic = new URLSearchParams(location.search).get("konu");
  if (requestedTopic && topics.includes(requestedTopic)) form.elements.topic.value = requestedTopic;
  if (isMeetingRequest) {
    const dateInput = form.elements.requestedDate;
    const formatTurkeyDate = value => {
      const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(value);
      const part = type => parts.find(item => item.type === type)?.value || "";
      return `${part("year")}-${part("month")}-${part("day")}`;
    };
    if (dateInput) {
      dateInput.min = formatTurkeyDate(new Date());
      dateInput.max = formatTurkeyDate(new Date(Date.now() + 180 * 86400000));
    }
  }
  const clearErrors = () => {
    form.querySelectorAll(".wf-error").forEach(item => item.remove());
    form.querySelectorAll('[aria-invalid="true"]').forEach(item => { item.removeAttribute("aria-invalid"); item.removeAttribute("aria-describedby"); });
  };
  const showError = (field, message) => {
    const input = form.elements[field];
    if (!input) return;
    const node = doc.createElement("p");
    node.className = "wf-error";
    node.id = input.id + "-error";
    node.setAttribute("role", "alert");
    node.textContent = message;
    input.setAttribute("aria-invalid", "true");
    input.setAttribute("aria-describedby", node.id);
    (field === "consent" ? form.querySelector("[data-consent-error]") : input).insertAdjacentElement("afterend", node);
  };
  const validate = data => {
    const errors = [];
    if (String(data.get("name") || "").trim().length < 2) errors.push(["name", "Lütfen adınızı ve soyadınızı yazın."]);
    if (String(data.get("company") || "").trim().length < 2) errors.push(["company", "Lütfen şirket adını yazın."]);
    const email = String(data.get("email") || "").trim();
    if (!email) errors.push(["email", "Kurumsal e-posta adresi gerekli."]);
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.push(["email", "Geçerli bir e-posta adresi yazın."]);
    if (!data.get("topic")) errors.push(["topic", "Lütfen ilgilendiğiniz konuyu seçin."]);
    if (isMeetingRequest) {
      const requestedDate = String(data.get("requestedDate") || "");
      const requestedTime = String(data.get("requestedTime") || "");
      if (!requestedDate) errors.push(["requestedDate", "Lütfen görüşme tarihini seçin."]);
      if (!requestedTime) errors.push(["requestedTime", "Lütfen görüşme saatini seçin."]);
      if (requestedDate && requestedTime) {
        const requestedAt = new Date(`${requestedDate}T${requestedTime}:00+03:00`);
        const latest = new Date(Date.now() + 180 * 86400000);
        if (Number.isNaN(requestedAt.getTime()) || requestedAt <= new Date()) errors.push(["requestedDate", "Gelecekte bir tarih ve saat seçin."]);
        else if (requestedAt > latest) errors.push(["requestedDate", "En fazla 180 gün sonrası için görüşme talep edebilirsiniz."]);
      }
    }
    if (String(data.get("message") || "").trim().length < 20) errors.push(["message", isMeetingRequest ? "Görüşme notunu en az 20 karakterle yazın." : "İhtiyacınızı en az 20 karakterle kısaca anlatın."]);
    if (data.get("consent") !== "on") errors.push(["consent", "Devam etmek için aydınlatma metnini okuduğunuzu onaylayın."]);
    return errors;
  };
  listen(form, "submit", event => {
    event.preventDefault();
    clearErrors();
    status.textContent = "";
    status.className = "form-status";
    const data = new FormData(form);
    const errors = validate(data);
    errors.forEach(item => showError(item[0], item[1]));
    if (errors.length) { form.elements[errors[0][0]].focus(); return; }
    if (String(data.get("website") || "").trim()) return;
    submitButton.disabled = true;
    const buttonLabel = submitButton.querySelector("span:last-child");
    if (buttonLabel) buttonLabel.textContent = "Gönderiliyor";
    const payload = {};
    data.forEach((value, key) => { payload[key] = value; });
    fetch(endpoint, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) })
      .then(response => response.json().catch(() => ({})).then(result => { if (!response.ok) throw new Error(result.message || "Form gönderilemedi."); return result; }))
      .then(() => { form.reset(); status.className = "form-status form-status--success"; status.textContent = isMeetingRequest ? "Görüşme talebiniz alındı. Ekibimiz tarih ve saat onayı için sizinle iletişime geçecek." : "Talebiniz alındı. Ekibimiz sizinle iletişime geçecek."; })
      .catch(() => { status.className = "form-status form-status--error"; status.setAttribute("role", "alert"); status.textContent = "Form gönderilemedi. Lütfen info@korelyazilim.net adresinden bize ulaşın."; })
      .finally(() => { submitButton.disabled = false; if (buttonLabel) buttonLabel.textContent = submitLabel; });
  });
}

function init() {
  if (state.initialized) return;
  state.initialized = true;
  state.reduced = reducedQuery.matches;
  initSmoothScroll();
  initHeader();
  initMenu();
  initHeadline();
  initHero();
  initHomeProductLinks();
  initContactPage();
  initReveals();
  initAccordions();
  initHomeScrollScenes();
  initProcessSteps();
  initSolutionScenes();
  initCodeCards();
  initMarquees();
  initFooterCanvas();
  initContactForm();
  requestAnimationFrame(() => ScrollTrigger.refresh());
}

function refresh() {
  state.lenis?.resize();
  ScrollTrigger.refresh();
}

function destroy() {
  while (cleanups.length) {
    try { cleanups.pop()(); } catch (error) { console.warn("Korel motion cleanup failed.", error); }
  }
  ScrollTrigger.getAll().forEach(trigger => trigger.kill());
  state.initialized = false;
}

window.KorelMotion = { init, refresh, destroy };
if (doc.readyState === "loading") doc.addEventListener("DOMContentLoaded", init, { once: true }); else init();
